import type { ProbeRun, ServiceReading, ServiceState } from "./services.ts";
import { looksLikeToken } from "./services-tokens.ts";

/**
 * Pure parsers for the read-only probes in `services.ts`. Each one maps a finished run (exit code, output, spawn error)
 * to a state. They never throw and never see a token: an account name that looks like a credential is dropped here.
 *
 * Order matters and is the same in every parser (docs/specs/machine-connected-services.md §4):
 *   1. the binary is missing → not_installed
 *   2. the run failed for a network reason → unknown. A network failure is never "logged out": gh, for one, reports an
 *      expired-looking "Failed to log in" line when it cannot reach the API to validate the token.
 *   3. an explicit signed-out message → not_logged_in
 *   4. the success shape → connected
 *   5. anything else → unknown
 *
 * Sample outputs: see the recorded cases in test/services-parse.test.ts, each with the doc or source it was taken from.
 */

const NETWORK_SIGNATURES = [
  "no such host",
  "dial tcp",
  "enotfound",
  "eai_again",
  "econnrefused",
  "econnreset",
  "enetunreach",
  "enetdown",
  "ehostunreach",
  "etimedout",
  "timeout",
  "timed out",
  "deadline exceeded",
  "could not resolve",
  "couldn't resolve",
  "network is unreachable",
  "network is down",
  "no route to host",
  "connection refused",
  "failed to connect",
  "unable to connect",
  "error connecting to",
  "check your internet connection",
  "fetch failed",
  "tls handshake",
  "proxyconnect",
  "getaddrinfo",
];

const EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const USERNAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

function combined(run: ProbeRun): string {
  return `${run.stdout}\n${run.stderr}`;
}

export function isNetworkFailure(text: string): boolean {
  const low = text.toLowerCase();
  return NETWORK_SIGNATURES.some((s) => low.includes(s));
}

/** An account is kept only when it looks like a plain username or email and is not token-shaped. */
export function safeAccount(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim();
  if (v.length === 0 || looksLikeToken(v)) return null;
  return EMAIL.test(v) || USERNAME.test(v) ? v : null;
}

/** The part of a run every parser checks before its own success or sign-out rules. Null means "go on to the rules". */
function early(run: ProbeRun, text: string): ServiceReading | null {
  if (run.spawnError === "ENOENT") return { state: "not_installed", account: null };
  if (run.spawnError === "ETIMEDOUT" || run.spawnError === "OTHER") return { state: "unknown", account: null };
  if (isNetworkFailure(text)) return { state: "unknown", account: null };
  return null;
}

function reading(state: ServiceState, account: string | null = null): ServiceReading {
  return { state, account };
}

/**
 * `gh auth status --hostname github.com` (cli.github.com/manual/gh_auth_status). Signed in: a line
 * "Logged in to github.com account <user>" (gh 2.x) or "Logged in to github.com as <user>" (older). Signed out: stderr
 * "You are not logged into any GitHub hosts. To log in, run: gh auth login". An expired or revoked token: "Failed to
 * log in to github.com account <user>" (source: cli/cli pkg/cmd/auth/status/status.go).
 */
export function parseGithubAuthStatus(run: ProbeRun): ServiceReading {
  const text = combined(run);
  const first = early(run, text);
  if (first) return first;
  // A signed-out reading needs a failing exit: a null or zero exit with the same words is not evidence of sign-out.
  const failed = run.exitCode !== null && run.exitCode !== 0;
  if (failed && /not logged into any GitHub hosts/i.test(text)) return reading("not_logged_in");
  // Several accounts: each has its own line and an "Active account" flag. A stale inactive account makes gh exit non-zero
  // but says nothing about the active one, so the active account's own line decides.
  const active = ghAccounts(text).find((a) => a.active === true);
  if (active) {
    if (active.ok && run.exitCode !== null) return reading("connected", safeAccount(active.account));
    return reading(failed ? "not_logged_in" : "unknown");
  }
  if (failed && /Failed to log in to /i.test(text)) return reading("not_logged_in");
  const m = /Logged in to \S+ (?:account|as) (\S+)/i.exec(text);
  if (run.exitCode === 0 && m) return reading("connected", safeAccount(m[1]));
  return reading("unknown");
}

/** The per-account blocks of `gh auth status`: the header line (ok or failed) and the block's "Active account" flag, when printed. */
function ghAccounts(text: string): { ok: boolean; account: string; active: boolean | null }[] {
  const out: { ok: boolean; account: string; active: boolean | null }[] = [];
  let cur: { ok: boolean; account: string; active: boolean | null } | null = null;
  for (const line of text.split("\n")) {
    const head = /(Logged in to|Failed to log in to) \S+ (?:account|as) (\S+)/i.exec(line);
    if (head) {
      cur = { ok: !/^failed/i.test(head[1]!), account: head[2]!, active: null };
      out.push(cur);
      continue;
    }
    const flag = /Active account:\s*(true|false)/i.exec(line);
    if (flag && cur && cur.active === null) cur.active = flag[1]!.toLowerCase() === "true";
  }
  return out;
}

/**
 * `vercel whoami --non-interactive` (vercel.com/docs/cli/whoami, global options). Piped output is the username and a
 * newline. Signed out: stdout "Logged out." and exit 1 (source: vercel/vercel packages/cli/src/commands/whoami).
 */
export function parseVercelWhoami(run: ProbeRun): ServiceReading {
  const text = combined(run);
  const first = early(run, text);
  if (first) return first;
  if (run.exitCode !== null && run.exitCode !== 0 && /^Logged out\./m.test(run.stdout)) return reading("not_logged_in");
  const lines = run.stdout.trim().split("\n");
  if (run.exitCode === 0 && lines.length === 1 && USERNAME.test(lines[0]!.trim())) {
    return reading("connected", safeAccount(lines[0]));
  }
  return reading("unknown");
}

/**
 * `supabase projects list -o json`. The CLI has no whoami; a JSON array means the stored token worked. Signed out:
 * stderr names the missing access token (representative text: supabase/cli reads SUPABASE_ACCESS_TOKEN or the stored
 * token; the docs page does not quote the message). The projects themselves are never kept.
 */
export function parseSupabaseProjectsList(run: ProbeRun): ServiceReading {
  const text = combined(run);
  const first = early(run, text);
  if (first) return first;
  const failed = run.exitCode !== null && run.exitCode !== 0;
  if (failed && /access token not provided/i.test(text)) return reading("not_logged_in");
  if (run.exitCode === 0) {
    try {
      if (Array.isArray(JSON.parse(run.stdout))) return reading("connected");
    } catch {
      // not JSON: fall through to unknown
    }
  }
  return reading("unknown");
}

/**
 * `fly auth whoami` (docs.fly.io/flyctl/auth-whoami): prints the signed-in user's email. Signed out: a "No access token
 * available" error that points at `flyctl auth login` (representative text; the docs page does not quote it).
 */
export function parseFlyAuthWhoami(run: ProbeRun): ServiceReading {
  const text = combined(run);
  const first = early(run, text);
  if (first) return first;
  const failed = run.exitCode !== null && run.exitCode !== 0;
  if (failed && /no access token available/i.test(text)) return reading("not_logged_in");
  const email = run.stdout.trim();
  if (run.exitCode === 0 && EMAIL.test(email)) return reading("connected", safeAccount(email));
  return reading("unknown");
}

/**
 * `gcloud auth list --filter=status:ACTIVE --format=value(account)` (cloud.google.com/sdk/gcloud/reference/auth/list).
 * An active account prints its name; no active account prints nothing. This reads local gcloud configuration only, so
 * it never makes a network call: an offline machine still reads as connected or not_logged_in, not unknown.
 */
export function parseGcloudAuthList(run: ProbeRun): ServiceReading {
  // No network-signature check here: nothing this command prints comes from the network, so a stderr line about DNS
  // cannot make a local read unknown. Only the spawn outcome and the exit code count.
  if (run.spawnError === "ENOENT") return reading("not_installed");
  if (run.spawnError) return reading("unknown");
  if (run.exitCode !== 0) return reading("unknown");
  const lines = run.stdout.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return reading("not_logged_in");
  return reading("connected", safeAccount(lines[0]));
}
